import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Bell,
  Smartphone,
  Calendar,
  Link2,
  Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatDate } from '../../lib/utils';
import { PushNotificationRecord } from '../../services/pushNotificationService';

interface ViewPushNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification: PushNotificationRecord | null;
  onDelete?: (notification: PushNotificationRecord) => void;
}

export function ViewPushNotificationModal({
  isOpen,
  onClose,
  notification,
  onDelete,
}: ViewPushNotificationModalProps) {
  if (!notification) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-white/20 rounded-2xl flex items-center justify-center border border-white/30">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Broadcast Push</h2>
                  <p className="text-white/80 text-sm">
                    Sent to topic "{notification.topic}"
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {onDelete && (
                  <Button
                    variant="secondary"
                    className="bg-white/20 text-white hover:bg-white/30"
                    onClick={() => onDelete(notification)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
              <div className="p-8 space-y-6">
                <div>
                  <p className="text-sm text-gray-500">Title</p>
                  <p className="text-xl font-semibold text-gray-900 mt-1">
                    {notification.title}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Message</p>
                  <p className="text-gray-800 mt-1 leading-relaxed">
                    {notification.body}
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Sent</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDate(notification.sentAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Devices targeted</p>
                      <p className="text-sm font-medium text-gray-900">
                        {typeof notification.targetedTokens === 'number'
                          ? notification.targetedTokens.toLocaleString()
                          : 'Not recorded'}
                      </p>
                    </div>
                  </div>
                  {notification.route && (
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Route</p>
                        <p className="text-sm font-medium text-gray-900">
                          {notification.route}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Badge className="bg-jamaica-green/10 text-jamaica-green border border-jamaica-green/20">
                    Topic: {notification.topic}
                  </Badge>
                  {notification.ctaLabel && (
                    <Badge className="bg-blue-50 text-blue-700 border border-blue-100">
                      CTA: {notification.ctaLabel}
                    </Badge>
                  )}
                  {notification.previewText && (
                    <Badge className="bg-purple-50 text-purple-700 border border-purple-100">
                      {notification.previewText}
                    </Badge>
                  )}
                </div>

                {notification.data && (
                  <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Data Payload
                    </p>
                    <pre className="text-xs text-gray-600 overflow-auto">
                      {JSON.stringify(notification.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="p-8 bg-gray-900/95 text-white flex items-center justify-center">
                <div className="w-full max-w-xs space-y-4">
                  <div className="bg-gradient-to-b from-gray-900 to-black rounded-3xl p-6 shadow-2xl border border-white/10">
                    <div className="flex items-center justify-between text-xs text-white/70 mb-6">
                      <span>Now</span>
                      <div className="flex items-center gap-2">
                        <span>JamPack'd</span>
                        <Bell className="w-4 h-4" />
                      </div>
                    </div>

                    <div className="bg-white/10 rounded-2xl p-4 space-y-3 backdrop-blur">
                      <div className="flex items-center gap-2 text-xs text-white/80">
                        <Smartphone className="w-4 h-4" />
                        <span>JamPack'd Mobile</span>
                      </div>
                      <div>
                        <p className="font-semibold text-base">{notification.title}</p>
                        <p className="text-sm text-white/80 mt-1">
                          {notification.body}
                        </p>
                      </div>
                      {notification.imageUrl && (
                        <img
                          src={notification.imageUrl}
                          alt="Preview"
                          className="rounded-xl border border-white/20 max-h-32 object-cover w-full"
                        />
                      )}
                      <Button
                        variant="secondary"
                        className="w-full bg-white text-gray-900 hover:bg-white/90"
                      >
                        {notification.ctaLabel || 'Open JamPack’d'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-white/60 text-center">
                    Preview simulates how users see this notification.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
